import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Box, Code, Group, Loader, Stack, Text, TextInput } from '@mantine/core';
import { useTRPC } from '../lib/trpc';
import { DataTable, type Column } from '../components/DataTable';
import classes from './EntityPage.module.css';

interface AuditRow {
  id: string;
  entityType: string;
  entityId: string | null;
  action: string;
  authorId: string | null;
  authorName: string | null;
  authorEmail: string | null;
  requestId: string | null;
  diff: unknown;
  createdAt: Date;
}

const ACTION_LABELS: Record<string, string> = {
  create: 'створення',
  update: 'зміна',
  delete: 'видалення',
  publish: 'публікація',
  revert: 'відкат',
  deploy: 'деплой',
  bulk: 'масова операція',
  login: 'вхід',
  role_change: 'зміна ролі',
  import: 'імпорт',
  unlock: 'розблокування',
  parse_apply: 'застосування парсингу',
};

const ACTION_COLOURS: Record<string, string> = {
  create: 'mint',
  update: 'azure',
  delete: 'rose',
  bulk: 'amber',
  revert: 'amber',
};

/**
 * Журнал змін (§8).
 *
 * Read-only, and not because nobody got round to the write screen: every entry is written
 * inside the transaction of the mutation it records. A log with an edit button is a log
 * whose contents mean nothing.
 */
/*
 * Колонку «Запит», фільтр за `requestId` і рядок із ним у картці прибрано 14.08 за списком
 * замовника: мокап журналу не має нічого з цього — це діагностика розробника, а не питання
 * оператора, і серед семи колонок вона займала місце тієї, яку читають.
 *
 * Сам `audit_log.request_id` лишається й далі йде в логи та Sentry: саме ним інцидент
 * звʼязується з дією. Прибрано перегляд, а не звʼязок.
 */
export function AuditPage(): React.JSX.Element {
  const trpc = useTRPC();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const list = useQuery(
    trpc.audit.list.queryOptions({
      ...(search ? { search } : {}),
      pagination: { limit: 100, direction: 'desc' },
    }),
  );

  const summary = useQuery(trpc.audit.summary.queryOptions({}));

  const detail = useQuery({
    ...trpc.audit.byId.queryOptions({ id: selectedId ?? '' }),
    enabled: selectedId !== null,
  });

  const columns: Column<AuditRow>[] = [
    {
      key: 'when',
      header: 'Коли',
      render: (row) => (
        <span className={classes.mono} data-testid={`audit-when-${row.id}`}>
          {new Date(row.createdAt).toLocaleString('uk-UA', { timeZone: 'Europe/Kyiv' })}
        </span>
      ),
    },
    {
      key: 'action',
      header: 'Дія',
      render: (row) => (
        <Badge
          size="sm"
          color={ACTION_COLOURS[row.action] ?? 'graphite'}
          data-testid={`audit-action-${row.id}`}
        >
          {ACTION_LABELS[row.action] ?? row.action}
        </Badge>
      ),
    },
    {
      key: 'entity',
      header: 'Сутність',
      render: (row) => (
        <span className={classes.mono} data-testid={`audit-entity-${row.id}`}>
          {row.entityType}
        </span>
      ),
    },
    {
      // The whole point of the screen: who did it. An entry with no author is a system job,
      // and saying «система» is more honest than leaving the column blank.
      key: 'author',
      header: 'Автор',
      render: (row) => (
        <span data-testid={`audit-author-${row.id}`}>{row.authorName ?? 'система'}</span>
      ),
    },
  ];

  const entry = detail.data;

  return (
    <Stack gap="md">
      <Group gap="md" align="flex-end">
        <TextInput
          label="Сутність"
          placeholder="brand, keyword…"
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value);
          }}
          data-testid="audit-search"
          style={{ width: 220 }}
        />
      </Group>

      {summary.data ? (
        <Group gap="lg" data-testid="audit-summary">
          <Text className={classes.dim}>
            записів:{' '}
            <span className={classes.mono} data-testid="audit-total">
              {summary.data.total}
            </span>
          </Text>
          {summary.data.byEntityType.slice(0, 5).map((row) => (
            <Text className={classes.dim} key={row.entityType}>
              {row.entityType}: <span className={classes.mono}>{row.count}</span>
            </Text>
          ))}
        </Group>
      ) : null}

      <DataTable
        rows={list.data?.items ?? []}
        columns={columns}
        loading={list.isPending}
        error={list.error}
        emptyMessage="Записів немає під ці фільтри."
        onRowClick={(row) => {
          setSelectedId(row.id);
        }}
        rowTestId={(row) => `audit-row-${row.id}`}
        testId="audit-table"
      />

      {selectedId === null ? null : (
        <Box className={classes.panel} data-testid="audit-detail">
          {detail.isPending ? (
            <Loader size="sm" />
          ) : entry === undefined ? null : (
            <Stack gap="sm">
              <Group justify="space-between">
                <Text className={classes.panelTitle}>
                  {entry.entityType} · {ACTION_LABELS[entry.action] ?? entry.action}
                </Text>
                <Text className={classes.dim}>{entry.authorEmail ?? 'система'}</Text>
              </Group>
              <Box>
                <Text className={classes.dim} mb={4}>
                  Що змінилося — лише ключі, які справді змінилися
                </Text>
                <Code block data-testid="audit-diff">
                  {JSON.stringify(entry.diff, null, 2)}
                </Code>
              </Box>
            </Stack>
          )}
        </Box>
      )}
    </Stack>
  );
}
